import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, ListChecks, Zap, Fingerprint, Banknote, Users, Compass, Wrench } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn, Eyebrow, Tag } from "@/components/Motion";
import { PANELS, IMAGES } from "@/lib/brand";

const FRAMEWORK = [
    { icon: Lightbulb, step: "01", title: "Idea", copy: "Say it out loud. Write it down. Every lane starts as a raw idea that finally gets taken seriously.", img: IMAGES.idea },
    { icon: ListChecks, step: "02", title: "Structure", copy: "The idea becomes a real project: scope, sequence, milestones. A blueprint instead of a vibe.", img: PANELS.puzzle },
    { icon: Zap, step: "03", title: "Action", copy: "Weekly moves with accountability behind them. Small, consistent reps that actually ship.", img: IMAGES.action },
    { icon: Fingerprint, step: "04", title: "Brand", copy: "Name, identity, voice, look. Your own lane — recognizable before you say a word.", img: PANELS.art },
    { icon: Banknote, step: "05", title: "Income", copy: "Offers, content, and creative ways for the work to pay. Creativity with a cash-flow plan.", img: PANELS.income },
];

const LANES = [
    { title: "Brand Identity", copy: "Naming, visuals, voice, and the story only you can tell." },
    { title: "Content Engine", copy: "A repeatable system — from MY 5 drops to long-form — that never starts from zero." },
    { title: "Offers & Income", copy: "Turning skill into services, products, and creative revenue streams." },
    { title: "Creative Direction", copy: "Real feedback and direction from people who have built before." },
];

export default function CreateBuild() {
    return (
        <div data-testid="create-build-page">
            <PageHero
                eyebrow="Create / Build"
                titleLines={["From raw idea", "to real lane."]}
                copy="The Network helps creatives and aspiring entrepreneurs move from ideas to structure, action, branding, content — and potential income. This is the framework."
            />

            {/* FRAMEWORK TIMELINE */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="framework-section">
                <div className="max-w-5xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">The Framework</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            Five moves, <span className="italic text-purp">in order.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-16 relative">
                        <div className="absolute left-[27px] md:left-[35px] top-2 bottom-2 w-px bg-gradient-to-b from-purp via-purp/40 to-transparent" />
                        <div className="space-y-10">
                            {FRAMEWORK.map((f, i) => (
                                <FadeIn key={f.step} delay={0.08 * i}>
                                    <div className="flex gap-6 md:gap-10 items-start" data-testid={`framework-step-${f.title.toLowerCase()}`}>
                                        <span className="relative shrink-0 w-14 h-14 md:w-[72px] md:h-[72px] rounded-2xl bg-panel border border-purp/40 card-glow flex items-center justify-center">
                                            <f.icon className="w-6 h-6 md:w-7 md:h-7 text-purp-neon" />
                                        </span>
                                        <div className="pt-1">
                                            <p className="font-mono text-[11px] tracking-[0.3em] text-slate-500">{f.step}</p>
                                            <h3 className="font-display font-extrabold uppercase text-3xl md:text-4xl leading-none mt-1">{f.title}</h3>
                                            <p className="mt-3 text-slate-400 text-sm md:text-base leading-relaxed max-w-xl">{f.copy}</p>
                                        </div>
                                        {f.img && (
                                            <img
                                                src={f.img}
                                                alt={`PURP ALERT mascot — ${f.title}`}
                                                loading="lazy"
                                                className="hidden md:block w-24 h-24 lg:w-28 lg:h-28 rounded-2xl object-cover border border-purp/30 ml-auto shrink-0"
                                            />
                                        )}
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* LANES */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="lanes-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8">
                    <FadeIn>
                        <Eyebrow className="mb-5">Build Your Lane</Eyebrow>
                        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.95]">
                            What we build <span className="italic text-purp">together.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {LANES.map((l, i) => (
                            <FadeIn key={l.title} delay={0.08 * i}>
                                <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-7 hover:border-purp/50 transition-colors duration-300" data-testid={`lane-card-${i}`}>
                                    <p className="font-mono text-[11px] tracking-[0.3em] text-purp-neon">0{i + 1}</p>
                                    <h3 className="mt-8 font-display font-bold uppercase text-2xl leading-tight">{l.title}</h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{l.copy}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ACCOUNTABILITY */}
            <section className="py-20 md:py-28 border-t border-white/10" data-testid="accountability-section">
                <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-6">
                    <FadeIn>
                        <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-8">
                            <Users className="w-7 h-7 text-purp-neon" />
                            <div className="mt-4"><Tag tone="volt">Forming Now</Tag></div>
                            <h3 className="mt-4 font-display font-extrabold uppercase text-3xl">Accountability Cohorts</h3>
                            <p className="mt-4 text-slate-400 text-sm md:text-base leading-relaxed">
                                Small circles of builders on a weekly cadence: state the move, make the move, report
                                the move. Honest feedback, real deadlines, nobody drifting.
                            </p>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <div className="h-full rounded-2xl bg-panel/75 backdrop-blur-md border border-white/10 p-8">
                            <Wrench className="w-7 h-7 text-purp-neon" />
                            <div className="mt-4"><Tag tone="volt">Growing Library</Tag></div>
                            <h3 className="mt-4 font-display font-extrabold uppercase text-3xl">Resource Hub &amp; Toolkits</h3>
                            <p className="mt-4 text-slate-400 text-sm md:text-base leading-relaxed">
                                Templates, checklists, brand starters, and content systems — the practical assets that
                                turn &ldquo;someday&rdquo; into a schedule.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 border-t border-white/10 relative overflow-hidden" data-testid="create-build-cta">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
                    <FadeIn>
                        <Compass className="w-8 h-8 text-purp-neon mx-auto" />
                        <h2 className="mt-5 font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Ready to find <span className="italic text-purp text-glow">your lane?</span>
                        </h2>
                        <Link to="/get-involved" data-testid="create-build-get-involved-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-purp px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_44px_rgba(168,85,247,0.7)]">
                            Start Building <ArrowRight className="w-4 h-4" />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
}
